import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import {
  isWebserverTelemetryEnabled,
  seeqFullReleaseIdentifier,
  seeqVersion,
} from '@/services/systemConfiguration.utilities';
import { subscribeToRequestLifecycle } from '@seeqdev/api';
import {
  AggregationTemporality,
  BatchLogRecordProcessor,
  DevConsoleExporter,
  PeriodicExportingMetricReader,
  createHttpClientRequestRecorder,
  getCommonAttributes,
} from '@seeqdev/telemetry';
import { OTLPLogExporter, OTLPMetricExporter } from '@seeqdev/telemetry/otlp';
import type { TelemetryConfig } from '@seeqdev/telemetry';

const isDev = process.env.NODE_ENV !== 'production';

// Same-origin endpoints nginx reverse-proxies to the OTel Collector.
const OTLP_METRICS_ENDPOINT = '/otel/v1/metrics';
const OTLP_LOGS_ENDPOINT = '/otel/v1/logs';

/**
 * Builds the metric readers for this session.
 *
 * Both dev and production export to the OTel Collector at `/otel/v1/metrics` so
 * RUM metrics can be validated locally against the Grafana stack. Only `url` is
 * configured; the export interval is left at the SDK default (60 s) and the
 * browser OTLP transport sends via keepalive fetch, which — together with the
 * `forceFlush()` on pagehide wired in `TelemetryProvider` — delivers the
 * end-of-session Core Web Vitals (INP/CLS finalize on hide).
 *
 * Dev additionally logs metrics to the browser console (`DevConsoleExporter`)
 * on a short interval for quick feedback while developing; production exports
 * only over OTLP.
 */
function createMetricReaders(): TelemetryConfig['readers'] {
  const readers: TelemetryConfig['readers'] = [
    new PeriodicExportingMetricReader({
      exporter: new OTLPMetricExporter({
        url: OTLP_METRICS_ENDPOINT,
        temporalityPreference: AggregationTemporality.DELTA,
      }),
    }),
  ];

  if (isDev) {
    readers.push(
      new PeriodicExportingMetricReader({ exporter: new DevConsoleExporter(), exportIntervalMillis: 10_000 }),
    );
  }

  return readers;
}

/**
 * Builds the log record processors for this session.
 *
 * Error- and warning-level log records (emitted by `@/utilities/logger` and the
 * global error handlers in `TelemetryProvider`) are batched and exported over
 * OTLP to the same-origin `/otel/v1/logs` endpoint, joining metrics on the OTel
 * pipeline (browser → Collector → Loki → Grafana). The browser OTLP transport
 * sends via keepalive fetch; the `forceFlush()` on pagehide wired in
 * `TelemetryProvider` drains any batched records at end of session.
 */
function createLogRecordProcessors(): TelemetryConfig['logRecordProcessors'] {
  return [new BatchLogRecordProcessor(new OTLPLogExporter({ url: OTLP_LOGS_ENDPOINT }))];
}

/**
 * Resolves the telemetry config for this session from server-provided values,
 * or `null` when webserver telemetry is disabled. Must be called only after
 * configuration has loaded (the getters throw otherwise).
 *
 * Webserver (RUM) telemetry is gated on its own server flag
 * (Features/Telemetry/Webserver/Enabled), independent of the Features/Telemetry/Enabled
 * flag that gates product-usage telemetry, so the two can be opted into separately.
 */
export function resolveTelemetryConfig(): TelemetryConfig | null {
  if (!isWebserverTelemetryEnabled()) {
    return null;
  }

  return {
    serviceName: 'webserver',
    serviceVersion: seeqFullReleaseIdentifier() ?? seeqVersion() ?? 'unknown',
    // deployment.environment is intentionally omitted: the OTel Collector stamps
    // it from the k8s cluster on ingest. Local runs simply have no environment.
    readers: createMetricReaders(),
    logRecordProcessors: createLogRecordProcessors(),
  };
}

let unsubscribeApiRequestTelemetry: (() => void) | null = null;

/**
 * Subscribes the HTTP client duration recorder to the api package's request lifecycle seam.
 * Call once at startup right after the axios interceptors are set up; it is safe before
 * telemetry initializes because the recorder drops events until the SDK is live. Requests are
 * attributed with the current route and whether this is a synthetic (headless renderer)
 * session, mirroring the state-telemetry attributes.
 *
 * Idempotent: a second call subscribes nothing and hands back the first call's unsubscribe, so a
 * double registration (hot reload, a second entry point) cannot double-count every request.
 * Calling the returned unsubscribe re-arms registration.
 *
 * `subscribeToRequestLifecycle(recorder)` is also the compile-time check that the telemetry
 * recorder's event types stay compatible with the api seam's; a required field added on the
 * telemetry side that the seam does not supply fails `type:check` here.
 */
export function registerApiRequestTelemetry(): () => void {
  if (unsubscribeApiRequestTelemetry) {
    return unsubscribeApiRequestTelemetry;
  }

  const unsubscribe = subscribeToRequestLifecycle(
    createHttpClientRequestRecorder({
      getAttributes: () => ({ ...getCommonAttributes(), 'session.synthetic': headlessRenderMode() }),
    }),
  );
  unsubscribeApiRequestTelemetry = () => {
    unsubscribeApiRequestTelemetry = null;
    unsubscribe();
  };

  return unsubscribeApiRequestTelemetry;
}

/** Test-only: forget the registration so a later call subscribes again. */
export function resetApiRequestTelemetryForTesting(): void {
  unsubscribeApiRequestTelemetry = null;
}
